import React, { useState } from 'react';
import {
    Box, IconButton, Typography, Pagination, Chip, Tooltip, Select, MenuItem,
    Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { humanizeEnum } from '../../utils/format';
import { ITask, TaskStatus, TaskPriority } from '../../interfaces/task.interface';
import { displayUsername } from '../../utils/userUtils';
import { projectTagLabel } from '../project/ProjectSelect'; // P1-A project

type ActorMode = 'assignee' | 'assigner' | 'admin';

interface Props {
    tasks: ITask[];
    loading: boolean;
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    mode: ActorMode;
    /** F8: blockedReason is passed when the new status is BLOCKED. */
    onStatusChange?: (task: ITask, status: TaskStatus, blockedReason?: string) => void;
    onEdit?: (task: ITask) => void;
    onDelete?: (task: ITask) => void;
}

const priorityColor: Record<TaskPriority, 'default' | 'warning' | 'error'> = {
    LOW: 'default',
    MEDIUM: 'warning',
    HIGH: 'error',
};

const statusColor: Record<TaskStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
    OPEN: 'default',
    IN_PROGRESS: 'info',
    DONE: 'success',
    CANCELLED: 'warning',
    BLOCKED: 'error', // F8
};

const allowedStatuses = (mode: ActorMode, current: TaskStatus): TaskStatus[] => {
    if (mode === 'assignee') return ['OPEN', 'IN_PROGRESS', 'BLOCKED', 'DONE'];
    return ['OPEN', 'IN_PROGRESS', 'BLOCKED', 'DONE', 'CANCELLED'];
};

/** F8: the status chip; a BLOCKED task shows its reason on hover. */
export const TaskStatusChip: React.FC<{ task: Pick<ITask, 'status' | 'blockedReason'> }> = ({ task }) => {
    const chip = <Chip label={humanizeEnum(task.status)} size="small" color={statusColor[task.status] ?? 'default'} />;
    if (task.status === 'BLOCKED' && task.blockedReason) {
        return <Tooltip title={`Blocked: ${task.blockedReason}`}><span data-testid="task-blocked-chip">{chip}</span></Tooltip>;
    }
    return chip;
};

const TaskListTable: React.FC<Props> = ({ tasks, loading, page, totalPages, onPageChange, mode, onStatusChange, onEdit, onDelete }) => {
    const canEdit = mode === 'assigner' || mode === 'admin';
    // F8: choosing BLOCKED asks for the reason first
    const [blocking, setBlocking] = useState<ITask | null>(null);
    const [reason, setReason] = useState('');
    const pick = (t: ITask, next: TaskStatus) => {
        if (!onStatusChange || next === t.status) return;
        if (next === 'BLOCKED') { setReason(''); setBlocking(t); return; }
        onStatusChange(t, next);
    };
    const columns: Column<ITask>[] = [
        { key: 'title', header: 'Title', priority: 1, helpKey: 'tasks.list.col.title', render: t => t.title },
        { key: 'who', header: mode === 'assignee' ? 'Assigned By' : 'Assignee', helpKey: 'tasks.list.col.who', render: t => displayUsername(mode === 'assignee' ? t.assignerName : t.assigneeName) || '—' },
        ...(tasks.some(t => t.projectId) ? [{ key: 'project', header: 'Project', helpKey: 'tasks.list.col.project', render: (t: ITask) => projectTagLabel(t.projectCode, t.projectName) }] : []), // P1-A project
        { key: 'due', header: 'Due', priority: 1, helpKey: 'tasks.list.col.due', render: t => t.dueDate ? format(new Date(t.dueDate), 'dd MMM yyyy') : '—' },
        { key: 'priority', header: 'Priority', helpKey: 'tasks.list.col.priority', render: t => <Chip label={humanizeEnum(t.priority)} size="small" color={priorityColor[t.priority]} /> },
        { key: 'status', header: 'Status', priority: 1, helpKey: 'tasks.list.col.status', render: t => onStatusChange ? (
            <Tooltip title={t.status === 'BLOCKED' && t.blockedReason ? `Blocked: ${t.blockedReason}` : ''}>
            <Select
                data-help="tasks.list.statusSelect"
                size="small"
                value={t.status}
                onChange={e => pick(t, e.target.value as TaskStatus)}
                disabled={t.status === 'CANCELLED' && mode === 'assignee'}
            >
                {allowedStatuses(mode, t.status).map(s => (
                    <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>
                ))}
                {!allowedStatuses(mode, t.status).includes(t.status) && (
                    <MenuItem key={t.status} value={t.status}>{humanizeEnum(t.status)}</MenuItem>
                )}
            </Select>
            </Tooltip>
        ) : (
            <TaskStatusChip task={t} />
        ) },
        { key: 'flags', header: 'Flags', helpKey: 'tasks.list.col.flags', render: t => (
            <>
                {t.isOverdue ? <Chip data-help="tasks.list.overdue" label="Overdue" color="error" size="small" /> : null}
                {t.status === 'BLOCKED' && t.blockedReason ? (
                    <Typography variant="caption" color="error" display="block">Blocked: {t.blockedReason}</Typography>
                ) : null}
            </>
        ) },
        { key: 'actions', header: 'Actions', role: 'actions', helpKey: 'tasks.list.col.actions', render: t => (
            <>
                {canEdit && onEdit && (
                    <Tooltip title="Edit"><IconButton data-help="tasks.list.action.edit" onClick={() => onEdit(t)} color="primary"><EditIcon /></IconButton></Tooltip>
                )}
                {canEdit && onDelete && (
                    <Tooltip title="Delete"><IconButton data-help="tasks.list.action.delete" onClick={() => onDelete(t)} color="error"><DeleteIcon /></IconButton></Tooltip>
                )}
            </>
        ) },
    ];

    return (
        <>
            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                <ResponsiveTable<ITask> data-help-section="tasks.list.table"
                    pageKey={`tasks.${mode}`}
                    columns={columns}
                    rows={tasks}
                    getRowKey={t => t.id}
                    emptyMessage="No tasks."
                />
            )}
            {blocking && (
                <Dialog open onClose={() => setBlocking(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>Mark &quot;{blocking.title}&quot; blocked</DialogTitle>
                    <DialogContent>
                        <TextField data-help="tasks.list.blockedReason" autoFocus label="What is it waiting on?" required fullWidth multiline minRows={2} sx={{ mt: 1 }}
                            value={reason} inputProps={{ maxLength: 300 }} onChange={e => setReason(e.target.value)} />
                    </DialogContent>
                    <DialogActions>
                        <Button data-help="tasks.list.blockedCancel" onClick={() => setBlocking(null)}>Cancel</Button>
                        <Button data-help="tasks.list.markBlocked" variant="contained" color="error" disabled={!reason.trim()} onClick={() => {
                            const t = blocking;
                            setBlocking(null);
                            onStatusChange?.(t, 'BLOCKED', reason.trim());
                        }}>Mark blocked</Button>
                    </DialogActions>
                </Dialog>
            )}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination data-help="tasks.list.pagination" count={totalPages} page={page + 1} onChange={(_, p) => onPageChange(p - 1)} />
                </Box>
            )}
        </>
    );
};

export default TaskListTable;
